import { states, type State } from '@buyerflo/core/schemas/generic'
import { OrgSettingsSchema } from '@buyerflo/core/schemas/org'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useOrgDetailsApi } from 'apiProviders'
import { useUserData } from 'auth'
import axios from 'axios'
import Loading from 'components/Loading'
import { SlotDatePicker } from 'components/datePicker'
import { SlotInput } from 'components/input'
import { useShowSnack } from 'components/snackbar'
import { useRef, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import Resizer from 'react-image-file-resizer'
import Button from 'components/button'
import { FormSection } from '@buyerflo/ui/components/FormSection'
import { Spinner, Text, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

/** Equal-width pair of fields; collapses to one column on narrow widths. */
const FieldRow = ({ children }: { children: React.ReactNode }) => (
    <XStack
        gap="$4"
        flexWrap="wrap"
    >
        {children}
    </XStack>
)

const cellProps = { flex: 1, minWidth: 200 } as const

const resizeFile = (file): Promise<any> =>
    new Promise(resolve => {
        Resizer.imageFileResizer(file, 600, 200, 'PNG', 100, 0, resolve, 'blob')
    })

const Body = ({ settings }: { settings: OrgSettingsSchema }) => {
    const trpc = useTRPC()
    const showSnack = useShowSnack()
    const { user, refreshUser, bustLogoCache } = useUserData()
    const inputFile = useRef<any>(null)
    const [uploadingLogo, setUploadingLogo] = useState(false)
    const updateOrgMutation = useMutation(trpc.org.updateOrgDetails.mutationOptions())
    const uploadLogoMutation = useMutation(trpc.org.uploadOrgLogo.mutationOptions())

    const { register, control, handleSubmit, formState, getValues, setValue, watch } =
        useForm<OrgSettingsSchema>({
            defaultValues: settings,
            resolver: zodResolver(OrgSettingsSchema),
        })

    const onSubmit: SubmitHandler<OrgSettingsSchema> = async data => {
        try {
            await updateOrgMutation.mutateAsync(data)
            showSnack('Organisation settings updated', 'success')
        } catch (e) {
            console.error(e)
            showSnack('Error saving organisation settings', 'danger')
        }
    }

    if (Object.keys(formState.errors).length > 0) {
        console.log('formState.errors', formState.errors)
    }

    return (
        <YStack
            gap="$5"
            maxWidth={560}
            paddingBottom="$8"
        >
            <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
            >
                <FormSection>
                    <YStack
                        gap="$3.5"
                        data-tour="org-details-form"
                        data-help-id="settings-org-form"
                    >
                        <FieldRow>
                            <Controller
                                name="tradingName"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Trading Name *"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="licenseeName"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Licensee Name"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                        </FieldRow>
                        <FieldRow>
                            <Controller
                                name="abn"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="ABN *"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="acn"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="ACN"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                        </FieldRow>
                        <FieldRow>
                            <Controller
                                name="licenceNumber"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Licence Number *"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="licenceExpiry"
                                control={control}
                                render={field => (
                                    <SlotDatePicker
                                        label="Licence Expiry"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                        </FieldRow>
                        <FieldRow>
                            <Controller
                                name="phone"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Phone Number *"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="fax"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Fax Number"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                        </FieldRow>
                        <FieldRow>
                            <Controller
                                name="email"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Email *"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="mobileNumber"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Mobile Number"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                        </FieldRow>
                        <FieldRow>
                            <Controller
                                name="website"
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Website"
                                        formControlProps={cellProps}
                                        {...field}
                                    />
                                )}
                            />
                            {/* Spacer keeps Website at half-width like the rest of the grid */}
                            <View {...cellProps} />
                        </FieldRow>

                        <View
                            marginTop="$2"
                            data-tour="org-save-button"
                        >
                            <Button
                                // @ts-ignore
                                type="submit"
                                onPress={handleSubmit(onSubmit)}
                                data-help-id="settings-org-save"
                                theme="primary"
                                alignSelf="flex-start"
                                paddingHorizontal="$5"
                            >
                                Save Changes
                            </Button>
                        </View>
                    </YStack>
                </FormSection>
            </form>

            <input
                type="file"
                id="file"
                ref={inputFile}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={async e => {
                    const file = e.target.files?.[0]
                    if (file) {
                        setUploadingLogo(true)
                        try {
                            const resized = await resizeFile(file)
                            const presignedUrl = await uploadLogoMutation.mutateAsync()
                            await axios.put(presignedUrl, resized, {
                                headers: {
                                    'Content-Type': resized.type,
                                },
                            })
                            await refreshUser()
                            bustLogoCache()
                            showSnack('Logo uploaded successfully', 'success')
                        } catch (err) {
                            console.error(err)
                            showSnack('Error uploading logo', 'danger')
                        } finally {
                            setUploadingLogo(false)
                        }
                    }
                }}
            />
            <YStack
                gap="$2"
                alignItems="flex-start"
                data-tour="logo-upload"
            >
                {user.orgLogo && (
                    <View
                        borderWidth={1}
                        borderColor="$borderColor"
                        borderRadius="$3"
                        backgroundColor="$background"
                        padding="$3"
                    >
                        <img
                            src={user.orgLogo}
                            alt="Organisation logo"
                            style={{
                                height: 50,
                                maxWidth: 300,
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    </View>
                )}
                <Button
                    disabled={uploadingLogo}
                    icon={uploadingLogo ? <Spinner size="small" /> : undefined}
                    onPress={() => inputFile.current!.click()}
                >
                    {user.orgLogo ? 'Replace Logo' : 'Upload Logo'}
                </Button>
                <Text
                    fontSize="$2"
                    color="$colorMuted"
                >
                    Recommended: 600×100px
                </Text>
            </YStack>

            {import.meta.env.DEV && (
                <>
                    <DevStateSelector />
                    <DevResetOrg />
                </>
            )}
        </YStack>
    )
}

const DevStateSelector = () => {
    const trpc = useTRPC()
    const { user, refreshUser } = useUserData()
    const showSnack = useShowSnack()
    const updateStateMutation = useMutation(trpc.org.updateOrgState.mutationOptions())

    const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newState = e.target.value as State
        try {
            await updateStateMutation.mutateAsync({ state: newState })
            showSnack(`State changed to ${newState}`, 'success')
            refreshUser()
        } catch (err) {
            showSnack('Failed to update state', 'danger')
        }
    }

    return (
        <YStack
            marginTop="$6"
            padding="$4"
            borderWidth={1}
            borderColor="$red8"
            borderRadius="$3"
            backgroundColor="$red2"
            maxWidth={500}
            gap="$2"
        >
            <Text fontWeight="bold" color="$red10">DEV ONLY: Change Org State</Text>
            <select
                value={(user as any).state || 'QLD'}
                onChange={handleChange}
                style={{ padding: '8px', maxWidth: '200px' }}
            >
                {states.map(s => (
                    <option key={s} value={s}>{s}</option>
                ))}
            </select>
        </YStack>
    )
}

const DevResetOrg = () => {
    const trpc = useTRPC()
    const { refreshUser } = useUserData()
    const showSnack = useShowSnack()
    const resetMutation = useMutation(trpc.e2eReset.resetOrg.mutationOptions())

    const handleReset = async () => {
        if (!window.confirm('Are you sure you want to reset this org? This will delete all purchasers, integrations, and onboarding progress.')) return
        try {
            const result = await resetMutation.mutateAsync()
            showSnack(`Org reset complete: ${result.reset.purchasersDeleted} purchasers deleted`, 'success')
            refreshUser()
        } catch (err) {
            showSnack('Failed to reset org', 'danger')
        }
    }

    return (
        <YStack
            marginTop="$4"
            padding="$4"
            borderWidth={1}
            borderColor="$red8"
            borderRadius="$3"
            backgroundColor="$red2"
            maxWidth={500}
            gap="$2"
        >
            <Text fontWeight="bold" color="$red10">DEV ONLY: Reset Org</Text>
            <Text fontSize="$2" color="$red10">
                Clears logo, onboarding, integrations, and deletes all purchasers.
            </Text>
            <Button
                theme="red"
                onPress={handleReset}
                style={{ maxWidth: '150px' }}
            >
                {resetMutation.isPending ? 'Resetting...' : 'Reset Org'}
            </Button>
        </YStack>
    )
}

const OrgDetails = () => {
    const orgDetails = useOrgDetailsApi()
    if (!orgDetails.data) {
        return <Loading />
    }
    return <Body settings={orgDetails.data} />
}

export default OrgDetails
